import { lazy, Suspense } from "react";
import { EMessageType, IMessage } from "types/Message.type";
import { IUser } from "types/User.types";

// Lazy load message components to reduce initial bundle size
const Message = lazy(() => import("components/_Pages/Explore/Message"));
const MessagePoll = lazy(() => import("components/_Pages/Explore/MessagePoll"));
const MessageEndorsement = lazy(
  () => import("components/_Pages/Explore/MessageEndorsement")
);
const MessageCorporateValue = lazy(
  () => import("components/_Pages/Explore/MessageCorporateValue")
);
const MessageCertificate = lazy(
  () => import("components/_Pages/Explore/MessageCertificate")
);
const MessageAcknowledge = lazy(
  () => import("components/_Pages/Explore/MessageAcknowledge")
);
const MessageWelcome = lazy(
  () => import("components/_Pages/Explore/MessageWelcome")
);
const MessageMagazine = lazy(
  () => import("components/_Pages/Explore/MessageMagazine")
);
const MessageBuySell = lazy(
  () => import("components/_Pages/Explore/MessageBuySell")
);
const MessageBenefit = lazy(
  () => import("components/_Pages/Explore/MessageBenefit")
);

interface LazyMessageRendererProps {
  message: IMessage;
  mentionUsers: IUser[];
  messages: IMessage[];
  setMessages: (messages: IMessage[]) => void;
  vendor?: any;
  stacked?: boolean;
}

const MessageSkeleton = () => (
  <div className="w-full animate-pulse rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
    <div className="flex items-center space-x-4">
      <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-600"></div>
      <div className="flex-1 space-y-2">
        <div className="h-3 w-32 rounded bg-gray-200 dark:bg-gray-600"></div>
        <div className="h-2 w-20 rounded bg-gray-200 dark:bg-gray-600"></div>
      </div>
    </div>
    <div className="mt-4 space-y-2">
      <div className="h-4 w-full rounded bg-gray-200 dark:bg-gray-600"></div>
      <div className="h-4 w-3/4 rounded bg-gray-200 dark:bg-gray-600"></div>
    </div>
  </div>
);

export default function LazyMessageRenderer({
  message,
  mentionUsers,
  messages,
  setMessages,
  vendor,
  stacked = false
}: LazyMessageRendererProps) {
  const renderMessage = () => {
    switch (message.type) {
      case EMessageType.POLL:
        return (
          <MessagePoll
            userToMention={mentionUsers}
            {...message}
            key={message.id}
            messages={messages}
            setMessages={setMessages}
          />
        );

      case EMessageType.ENDORSEMENT:
        return (
          <MessageEndorsement
            {...message}
            key={message.id}
            setMessages={setMessages}
            messages={messages}
          />
        );

      case EMessageType.CORPORATE_VALUE:
        return (
          <MessageCorporateValue
            userToMention={mentionUsers}
            {...message}
            key={message.id}
            messages={messages}
            setMessages={setMessages}
            compact={stacked}
          />
        );

      case EMessageType.CERTIFICATE:
        if (vendor?.enableCertificates) {
          return (
            <MessageCertificate
              userToMention={mentionUsers}
              {...message}
              key={message.id}
              messages={messages}
              setMessages={setMessages}
              compact={stacked}
            />
          );
        }
        break;

      case EMessageType.BIRTHDAY:
      case EMessageType.ANNIVERSARY:
        return (
          <MessageAcknowledge
            userToMention={mentionUsers}
            {...message}
            key={message.id}
            messages={messages}
            setMessages={setMessages}
            compact={stacked}
          />
        );

      case EMessageType.NEW_USERS:
        return (
          <MessageWelcome
            userToMention={mentionUsers}
            {...message}
            key={message.id}
            messages={messages}
            setMessages={setMessages}
            compact={stacked}
          />
        );

      case EMessageType.MAGAZINE:
        return (
          <MessageMagazine
            userToMention={mentionUsers}
            {...message}
            key={message.id}
            messages={messages}
            setMessages={setMessages}
            compact={stacked}
          />
        );

      case EMessageType.BUY_SELL:
        return (
          <MessageBuySell
            userToMention={mentionUsers}
            {...message}
            key={message.id}
            messages={messages}
            setMessages={setMessages}
          />
        );

      case EMessageType.BENEFIT:
        return (
          <MessageBenefit
            userToMention={mentionUsers}
            {...message}
            key={message.id}
            messages={messages}
            setMessages={setMessages}
          />
        );

      default:
        return (
          <Message
            userToMention={mentionUsers}
            {...message}
            key={message.id}
            messages={messages}
            setMessages={setMessages}
            tags={message.tags}
            timesSeen={message.timesSeen}
            stacked={stacked}
          />
        );
    }
  };

  return <Suspense fallback={<MessageSkeleton />}>{renderMessage()}</Suspense>;
}
